import React, { useCallback, useMemo } from "react";
import TimePicker from "./TimePicker";
import {
  getPeriodFromDate,
  HourMinuteOption,
  parseTimeFromDate,
  Period,
  PERIODS,
  TimeZoneValue,
  toStandardHours,
} from "./utils";

export interface TimePickerFromDateProps {
  id: string;
  date: Date;
  onChange: (date: Date) => void;
  utc?: boolean;
  className?: string;
  timeZone?: TimeZoneValue | null;
  timeZones?: TimeZoneValue[];
  onTimeZoneChange?: (timeZone: TimeZoneValue) => void;
}

/**
 * Time picker that reads and emits native JS `Date` objects.
 *
 * Use this when your time state is a native `Date` instance (common in video
 * playback and calendar components). Seconds and milliseconds are always
 * reset to 0 on change. Optionally supports timezone selection and UTC mode.
 */
export const TimePickerFromDate = ({
  id,
  date,
  onChange,
  utc,
  className,
  timeZone,
  timeZones,
  onTimeZoneChange,
}: TimePickerFromDateProps): JSX.Element => {
  const derived = useMemo(() => parseTimeFromDate(date, !!utc), [date, utc]);

  const handleHoursChange = useCallback(
    ({ value: hours }: HourMinuteOption) => {
      const newDate = new Date(date);
      const p = getPeriodFromDate(date);
      newDate.setHours(toStandardHours(hours, p));
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      onChange(newDate);
    },
    [date, onChange]
  );

  const handleMinutesChange = useCallback(
    ({ value: minutes }: HourMinuteOption) => {
      const newDate = new Date(date);
      newDate.setMinutes(minutes);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      onChange(newDate);
    },
    [date, onChange]
  );

  const handleTimePeriodChange = useCallback(
    (period: Period) => {
      const newDate = new Date(date);
      const hrs = newDate.getHours();

      if (period === PERIODS.AM && hrs > 12) {
        newDate.setHours(hrs - 12);
      } else if (period === PERIODS.PM && hrs <= 12) {
        newDate.setHours(hrs + 12);
      }
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      onChange(newDate);
    },
    [date, onChange]
  );

  return (
    <TimePicker
      id={id}
      hours={derived.hours}
      minutes={derived.minutes}
      period={derived.period}
      onHoursChange={handleHoursChange}
      onMinutesChange={handleMinutesChange}
      onTimePeriodChange={handleTimePeriodChange}
      className={className}
      timeZone={timeZone}
      timeZones={timeZones}
      onTimeZoneChange={onTimeZoneChange}
    />
  );
};

export default TimePickerFromDate;
